import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import { toast } from 'react-toastify'

import { Divider, Typography } from '@material-ui/core'
import { Button, Checkbox } from 'ui/components'
import ButtonPlanList from 'ui/components/plan/ButtonPlanList'

import { fetchOrganization } from 'redux/organization/actions'

import { getOrganization, putOrganization } from 'services/apiMotorBgc'
import reportError from 'utils/errorReporter'
import GovBrAccountForm from 'ui/components/GovBrAccountForm'
import { formatGovBrAccountInput } from 'utils/functions'
import { PANEL_ENVS } from 'utils/constants/environments'

import styles from './styles'

export default function () {
  const classes = styles()
  const dispatch = useDispatch()

  const formEditedInitialState = {
    usePlan: false,
    cpf: false,
    password: false,
    useGovBrAccount: false,
    planDefaultCpf: false,
    planDefaultCnpj: false,
  }

  const apiKey = useSelector((state) => state.auth.api_key)
  const userId = useSelector((state) => state.auth.sub)
  const userOrgId = useSelector((state) => state.user.org_id)

  const [organization, setOrganization] = useState(null)
  const [saveDisabled, setSaveDisabled] = useState(true)
  const [hasFormError, setHasFormError] = useState(false)
  const [editedFormState, setEditedFormState] = useState(formEditedInitialState)

  useEffect(() => {
    if (_.every(editedFormState, (v) => v === false)) {
      setSaveDisabled(true)
      return
    }
    if (editedFormState.cpf || editedFormState.password) {
      setSaveDisabled(hasFormError)
      return
    }

    setSaveDisabled(false)
  }, [hasFormError, editedFormState])

  const getAndSetOrg = async (options = {}) => {
    let res = null

    try {
      res = await getOrganization(
        apiKey,
        userId,
        {
          orgId: options.orgId,
          withConfig: true,
          withConfigGovBrAccount: true,
        },
      )
    } catch (err) {
      reportError(err, 'Erro ao obter organização')
      return
    }

    setOrganization(res.data)
  }

  useEffect(() => {
    getAndSetOrg({ orgId: userOrgId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    const parseEditedOrgData = () => {
      const { config } = organization

      const newConfig = {}
      if (editedFormState.usePlan) newConfig.use_plan = config.use_plan
      if (editedFormState.planDefaultCpf) newConfig.default_plan_cpf_id = config.default_plan_cpf.plan_id
      if (editedFormState.planDefaultCnpj) newConfig.default_plan_cnpj_id = config.default_plan_cnpj.plan_id
      if (editedFormState.useGovBrAccount) newConfig.use_gov_br_account = config.use_gov_br_account
      if (editedFormState.cpf || editedFormState.password) newConfig.gov_br_account = formatGovBrAccountInput(config.gov_br_account)

      return {
        config: newConfig,
      }
    }

    try {
      await putOrganization(
        apiKey,
        userId,
        {
          orgId: userOrgId,
          orgData: parseEditedOrgData(),
        },
      )
    } catch (err) {
      reportError(err, 'Erro ao salvar')
      return
    }

    toast.success('Salvo com sucesso!')
    setEditedFormState(formEditedInitialState)
    dispatch(fetchOrganization())
  }

  const handleCheckUsePlan = (checked) => {
    const newOrg = _.cloneDeep(organization)
    newOrg.config.use_plan = checked
    setOrganization(newOrg)
    setEditedFormState({ ...editedFormState, usePlan: true })
  }

  const handleUpdateConfig = (newConfig) => {
    setOrganization({ ...organization, config: newConfig })
  }

  const handleEditGovBrForm = (formName) => {
    setEditedFormState({ ...editedFormState, [formName]: true })
  }

  const handlePlanSelectedCpf = (plan) => {
    const newOrg = _.cloneDeep(organization)
    newOrg.config.default_plan_cpf = plan
    setOrganization(newOrg)
    setEditedFormState({ ...editedFormState, planDefaultCpf: true })
  }

  const handlePlanSelectedCnpj = (plan) => {
    const newOrg = _.cloneDeep(organization)
    newOrg.config.default_plan_cnpj = plan
    setOrganization(newOrg)
    setEditedFormState({ ...editedFormState, planDefaultCnpj: true })
  }

  const renderOrgName = () => {
    return (
      <Typography>
        <span>Nome da Organização:</span>
        {' '}
        <strong>{organization.name}</strong>
      </Typography>
    )
  }

  const renderOrgUsePlan = () => {
    return (
      <Checkbox
        value={_.get(organization, 'config.use_plan')}
        label="Obrigar o uso dos planos da organização para todos os usuários"
        onClick={handleCheckUsePlan}
      />
    )
  }

  const renderBtnPlanListCpf = () => {
    return (
      <div className={classes.btnPlanContainer}>
        <Typography>Plano do CPF:</Typography>
        <ButtonPlanList
          disabled={!_.get(organization, 'config.use_plan')}
          docType={PANEL_ENVS.CPF}
          filterVisibleToOrganization={true}
          onSelectPlan={handlePlanSelectedCpf}
          showBtnCreate={false}
          showBtnDelete={false}
          showBtnEdit={false}
          showBtnSelect={true}
          showBtnView={true}
          toOrganization={true}
        />
      </div>
    )
  }

  const renderBtnPlanListCnpj = () => {
    return (
      <div className={classes.btnPlanContainer}>
        <Typography>Plano do CNPJ:</Typography>
        <ButtonPlanList
          disabled={!_.get(organization, 'config.use_plan')}
          docType={PANEL_ENVS.CNPJ}
          filterVisibleToOrganization={true}
          onSelectPlan={handlePlanSelectedCnpj}
          showBtnCreate={false}
          showBtnDelete={false}
          showBtnEdit={false}
          showBtnSelect={true}
          showBtnView={true}
          toOrganization={true}
        />
      </div>
    )
  }

  const renderGovBrForm = () => {
    return (
      <div className={classes.govBrContainer}>
        <GovBrAccountForm
          config={organization?.config}
          onUpdateConfig={handleUpdateConfig}
          onUpdateHasError={(hasError) => setHasFormError(hasError)}
          onEditForm={handleEditGovBrForm}
          isOrganization={true}
        />
      </div>
    )
  }

  const renderBtnSave = () => {
    return (
      <Button disabled={saveDisabled} onClick={handleSave}>
        Salvar
      </Button>
    )
  }

  if (_.isEmpty(organization)) return null

  return (
    <div className={classes.container}>
      {renderOrgName()}

      <Divider />

      <Typography>Planos</Typography>
      {renderOrgUsePlan()}
      {renderBtnPlanListCpf()}
      {renderBtnPlanListCnpj()}

      <Divider />

      {renderGovBrForm()}

      <Divider />

      {renderBtnSave()}
    </div>
  )
}
